import { ImageName } from "@mcleod/images";
import { Alignment, Api, CurrencySettings, DisplayType, FieldUpdateEvent, ModelRow, StringUtil } from "@mcleod/core";
import {
    ChangeEvent, ClickEvent, Cursor, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, Image, Label, Layout,
    LookupModelSelectionEvent, Panel, Table, TextboxProps
} from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { AssetUtil } from "@mcleod/general";
import { AutogenLayoutBillingOverview } from "./autogen/AutogenLayoutBillingOverview";
import { DistanceAlertUtil } from "@mcleod/dispatch/src/DistanceAlertUtil";
import { BillingCommentsQuickInfo } from "./BillingCommentsQuickInfo";
import { IntBillCommentsQuickInfo } from "./IntBillCommentsQuickInfo";
import { ExtBillCommentsQuickInfo } from "./ExtBillCommentsQuickInfo";
import { createCommentModelRows } from "./InvoiceProcessingUtil";
import { InvoiceProcessingChargesQuickInfo } from "./InvoiceProcessingChargesQuickInfo";
import { Billing } from "./Billing";

export class BillingOverview extends AutogenLayoutBillingOverview {
    private distanceAlertUtil = new DistanceAlertUtil();
    private isAsset: boolean = false;
    private _ySplit: boolean = false;

    override onLoad() {
        this.textboxContactName.captionLabel.color = "primary.reverse";
        this.textboxSalespersonId.captionLabel.color = "primary.reverse";
        this.textboxName.captionLabel.color = "primary.reverse";
        this.panelDataFusion.visible = false;
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.panelTax.visible = true;
        }
    }

    async sourceOrdersAfterExecution(event) {
        const row = this.sourceOrders.activeRow;
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.setCarrierRateData();
        }
        this.isAsset = AssetUtil.isAssetMoveType(row);
    }

    async sourceBillingAfterExceution(event) {
        const row = this.mainDataSource.activeRow;
        this.textboxCommodity.text = row?.getFirstLookupModelData("commodity")?.get("descr");
        //this.addReferenceNumberTextboxes();
        if (event.getAction() === DataSourceAction.SEARCH) {
            const customerId = row?.get("customer_id");
            if (customerId != null) {
                await this.sourceCustomer.search({ "id": customerId });
            }

            if (row) {
                row.addAfterFieldUpdateListener((event: FieldUpdateEvent) => this.billingFieldUpdated(event))
            }
        }
        if (this.mainDataSource.activeRow?.get("order_id")) {
            await this.sourceOrders.search({ id: this.mainDataSource.activeRow?.get("order_id") });
        }
    }

    billingFieldUpdated(event: FieldUpdateEvent) {
        if ("customer_id" == event.fieldName && StringUtil.isEmptyString(event.newValue))
            this.customerChanged(null);
        else if ("commodity" == event.fieldName)
            this.textboxCommodity.text = event.originator?.text || null;
    }

    textboxNameOnDataDisplay() {
        CommonTooltips.setTooltipContentLayout(this.textboxName, this.activeRow?.get("customer_id"), "lme/ar/CustomerQuickInfo");
    }

    customerChanged(customerData: ModelRow<any>) {
        this.sourceCustomer.data = [];
        if (customerData != null) {
            this.sourceCustomer.data = [customerData];
            this.sourceCustomer.setRowIndexWithoutUpdate(0);
        }
        this.sourceCustomer.displayDataInBoundComponents();
        this.setBridgeCustomerLabel();
    }

    setCarrierRateData() {
        const moveId = this.sourceOrders.activeRow?.get("movement.id");
        const orderFreightCharge = this.sourceOrders.activeRow?.getCurrency("freight_charge");
        const orderCurrencyCode = orderFreightCharge?.getCurrencyCode();
        const orderConversionDate = orderFreightCharge?.getConversionDate();
        let moveCurrencyCode: string = null;
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            const moveCarrierPay = this.sourceOrders.activeRow?.getCurrency("movement.override_pay_amt");
            moveCurrencyCode = moveCarrierPay != null ? moveCarrierPay.getCurrencyCode() : orderCurrencyCode;
        }
        if (moveId != null) {
            this.sourceCalculateOverridePay.search({
                "movement_id": moveId,
                "order_id": this.sourceOrders.activeRow?.get("id"),
                "calculate_order_total": true,
                "move_currency_code": moveCurrencyCode,
                "conversion_date": orderConversionDate
            });
        }
    }

    /** This is an event handler for the afterExecution event of sourceDistanceAlert. */
    sourceDistanceAlertAfterExecution(event: DataSourceExecutionEvent) {
        if (this.distanceAlertUtil.unclearedDistanceAlertsExist(this.sourceDistanceAlert)) {
            const row = this.sourceDistanceAlert.data[this.sourceDistanceAlert.data.length - 1];
            this.billingDistanceDangerImage.visible = true;
            for (const component of this.panelBillingDistance.components) {
                const label = component as Label;
                if (!StringUtil.isEmptyString(label.field)) {
                    label.color = "warning";
                    this.billingDistanceDangerImage.color = "warning";
                    this.labelBillingDistance.text = "0.0";
                    this.labelBillDistanceUm.color = "warning";
                }
                this.createLabelHoverToDiscover(label, row);
                label.allowTooltip = true;
            }
        } else {
            this.setDistanceWarningComponentsToDefault(this.billingDistanceDangerImage, this.panelBillingDistance);
        }
    }

    async sourceOrderOnDisplay() {
        const movementId = this.sourceOrders.activeRow?.get("movement.id") as string;
        const orderId = this.sourceOrders.activeRow?.get("id") as string;
        const customerId = this.mainDataSource.activeRow?.get("customer_id") as string;
        const companyId = this.sourceOrders.activeRow?.get("company_id") as string;
        const pbLtl = this.sourceOrders.activeRow?.get("is_brokerage_ltl");
        const hasIntercompany = this.sourceOrders.activeRow?.get("has_intercompany") as boolean;
        const hasICerror = this.sourceOrders.activeRow?.get("has_ic_error") as boolean;

        const layoutOrderDataFusionSummary = this.layoutOrderDataFusionSummary;
        if (movementId && orderId && customerId) {
            layoutOrderDataFusionSummary.movementId = movementId;
            layoutOrderDataFusionSummary.orderId = orderId;
            layoutOrderDataFusionSummary.customerId = customerId;
            layoutOrderDataFusionSummary.companyId = companyId;
            layoutOrderDataFusionSummary.hasIntercompany = hasIntercompany;
            layoutOrderDataFusionSummary.hasICError = hasICerror;

            const filter = [
                {
                    movement_id: movementId,
                    order_id: orderId,
                    customer_id: customerId,
                    is_brokerage_ltl: pbLtl
                }
            ];
            const body = JSON.stringify(filter);
            await this.delay(1000);
            Api.search("lme/dispatch/order-datafusion-summary", body).then(response => {
                if (response) {
                    layoutOrderDataFusionSummary.setEdiData(response.data[0]);
                    this.panelDataFusion.visible = layoutOrderDataFusionSummary.displayingData;
                }
            });
        }
    }

    sourceBillingOnDisplay() {
        if (this.mainDataSource.activeRow) {
            this.textboxTotalCharge.tooltipDynamicContent = async () => {
                const layout = await Layout.getLoadedLayout("lme/ar/InvoiceProcessingChargesQuickInfo") as InvoiceProcessingChargesQuickInfo;
                await layout.sourceBilling.search({ id: this.mainDataSource?.activeRow?.get("id") });
                await layout.sourceOtherChargeBill.search({ invoice_id: this.mainDataSource?.activeRow?.get("id") });
                if (!this.isAsset) {
                    await layout.sourceInvoiceMargins.search({ order_id: this.mainDataSource?.activeRow?.get("order_id") });
                }
                return layout;
            };
        }
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async showErrorPanel(ySplit: boolean) {
        let showPanel = false;
        const activeRow = this.mainDataSource.activeRow;

        if (activeRow) {
            showPanel = this.updateVisibilityBasedOnFlags(activeRow) || showPanel;
        }

        if (await this.setCommentsInfo()) {
            this.imageComments.visible = true;
            this.setCommentsQuickInfo(this.imageComments);
            showPanel = true;
        }

        if (ySplit) {
            this.imageYSplit.visible = true;
            showPanel = true;
        }

        if (this.sourceCustomer?.activeRow?.get("summary_billing") === "Y") {
            this.imageSummaryInvoice.visible = true;
            showPanel = true;
        }

        await this.setNotificationsImage();

        if (showPanel) {
            this.panelIcons.visible = true;
        }
    }

    private updateVisibilityBasedOnFlags(activeRow: ModelRow<any>): boolean {
        let showPanel = false;

        const flagMappings = [
            { field: "is_split_bill", image: this.imageSplitBill },
            { field: "printed", image: this.imagePrinted },
            { field: "rebilled", image: this.imageRebill },
            { field: "is_edi_customer", image: this.imageDatafusionCustomer }
        ];

        flagMappings.forEach(({ field, image }) => {
            if (activeRow?.get(field) === "Y" || activeRow?.get(field) === true) {
                image.visible = true;
                showPanel = true;
            }
        });

        if (activeRow?.get("is_quick_pay")) {
            this.imageQuickBill.visible = true;
            this.imageQuickBill.tooltip = activeRow?.get("drs_payee_term_is_quick_pay") ? "Triumph Quick Pay" : "Quick Pay";
            showPanel = true;
        }

        return showPanel;
    }

    async setCommentsInfo(): Promise<boolean> {
        const orderId = this.mainDataSource.activeRow?.get("order_id");
        const customerId = this.mainDataSource.activeRow?.get("customer_id");
        let rowCount = 0;

        if(orderId && customerId) {
            const response = await Api.search("lme/ar/billing-comments", { order_id: orderId, customer_id: customerId })
            rowCount = response.data[0]["number_of_comments"];
        }

        return rowCount !== 0;
    }

    setCommentsQuickInfo(image: Image) {
        image.tooltipDynamicContent = async () => {
            const commentLayout = await Layout.getLoadedLayout("lme/ar/BillingCommentsQuickInfo") as BillingCommentsQuickInfo;
            await commentLayout.sourceBillingComments.search({
                order_id: this.mainDataSource.activeRow?.get("order_id"),
                customer_id: this.mainDataSource.activeRow?.get("customer_id")
            });

            const intRows = commentLayout.sourceBillingComments.data[0].get("int_bill_comments");
            const extRows = commentLayout.sourceBillingComments.data[0].get("ext_bill_comments");

            if (this.areCommentsEmpty(intRows, extRows)) {
                return null;
            }
            this.populateCommentLayout(intRows, commentLayout.layoutIntBillCommentsQuickInfo as IntBillCommentsQuickInfo);
            this.populateCommentLayout(extRows, commentLayout.layoutExtBillCommentsQuickInfo as ExtBillCommentsQuickInfo);
            return commentLayout;
        };
    }

    private areCommentsEmpty(intRows: any[], extRows: any[]): boolean {
        return intRows.length === 0 && extRows.length === 0;
    }

    private populateCommentLayout(rows: any[], layout: IntBillCommentsQuickInfo | ExtBillCommentsQuickInfo) {
        if (rows.length > 0) {
            let table: Table;
            if (layout instanceof IntBillCommentsQuickInfo) {
                table = layout.tableIntBillComments;
            } else {
                table = layout.tableExtBillComments;
            }
            const modelRows = createCommentModelRows(rows);
            table.dataSource.setRowsAndMode(DataSourceMode.NONE, modelRows);
        } else {
            layout.visible = false;
        }
    }

    async setNotificationsImage() {
        const exceptions = this.getExceptions();
        const numberOfExceptions = exceptions.length;
        const printed = this.mainDataSource.activeRow?.get("printed") === "Y";
        const orderId = this.mainDataSource.activeRow?.get("order_id");
        if(orderId) {
            const response = await Api.search("lme/ar/imaging-documents-lookup", { row_id: orderId, row_type: "O" });
            const imageMissingImage = this.imageNotifications;

            if (this.hasMissingImages(response)) {
                const { missingImages, missingImageCount } = this.getMissingImagesData(response);
                const totalExceptionCount = numberOfExceptions + missingImageCount + (printed ? 1 : 0);
                imageMissingImage.visible = true;
                this.panelIcons.visible = true;

                const textboxPropsArray = this.buildTextboxPropsArray(missingImages, exceptions, printed, totalExceptionCount);
                this.setImageProperties(imageMissingImage, numberOfExceptions, printed);
                CommonTooltips.setTooltipContentTextboxes(imageMissingImage, () => textboxPropsArray);
            } else if (numberOfExceptions > 0) {
                this.setExceptions(exceptions, printed);
            } else {
                imageMissingImage.visible = false;
            }
        }
    }

    private getExceptions(): string[] {
        const exceptions: string[] = [];
        const totalCharges = this.mainDataSource.activeRow?.get("total_charges");

        // Check for zero or negative total charges
        if (!totalCharges || totalCharges?.amount <= 0) {
            exceptions.push("Invoice total charges are 0.");
        }

        const orderId = this.mainDataSource.activeRow?.get("order_id");
        if (StringUtil.isEmptyString(orderId)) {
            exceptions.push("Order ID is required.");
        }
        return exceptions;
    }

    private hasMissingImages(response: any): boolean {
        return Array.isArray(response.data) && response.data.length > 0 && response.data[0].imaging_permitted &&
            response.data[0].required_image_missing_count > 0;
    }

    private getMissingImagesData(response: any) {
        const missingImageCount = response.data[0].required_image_missing_count;
        const images = response.data[0].images;
        const missingImages = images.filter(image => image.is_missing === "Y");
        return { missingImages, missingImageCount };
    }

    private buildTextboxPropsArray(
        missingImages: any[],
        exceptions: string[],
        printed: boolean,
        totalExceptionCount: any
    ): Partial<TextboxProps>[] {
        const textboxPropsArray: Partial<TextboxProps>[] = [totalExceptionCount];
        let totalIndex = -1;

        missingImages.forEach((image, index) => {
            textboxPropsArray[index] = {
                caption: "Missing Image",
                text: image.description,
                displayType: DisplayType.STRING,
                rowBreak: true,
                fillRow: true
            };
            totalIndex = index;
        });

        if (printed) {
            textboxPropsArray[totalIndex + 1] = {
                caption: "Status",
                text: "Invoice has been printed",
                displayType: DisplayType.STRING,
                rowBreak: true,
                fillRow: true
            };
            totalIndex++;
        }

        exceptions.forEach((exception, index) => {
            textboxPropsArray[totalIndex + 1] = {
                caption: "Invoice Exception",
                text: exception,
                displayType: DisplayType.STRING,
                rowBreak: true,
                fillRow: true
            };
            totalIndex++;
        });

        return textboxPropsArray;
    }

    private setImageProperties(image: Image, numberOfExceptions: number, printed: boolean) {
        if (numberOfExceptions > 0) {
            image.name = ImageName.circleError;
            image.color = "error.reverse";
        } else if (printed) {
            image.color = "primary.reverse";
        } else {
            image.color = "warning.reverse";
        }
    }

    setExceptions(exceptions: any[], printed: boolean) {
        const totalExceptionCount:any = exceptions.length + (printed ? 1 : 0);
        const textboxPropsArray: Partial<TextboxProps>[] = [totalExceptionCount];
        this.imageNotifications.visible = true;
        this.imageNotifications.name = ImageName.circleError;
        this.imageNotifications.color = "error.reverse";
        this.panelIcons.visible = true;

        exceptions.forEach((exception, index) => {
            textboxPropsArray[index] = {
                caption: "Invoice Exception",
                text: exception,
                displayType: DisplayType.STRING,
                rowBreak: true,
                fillRow: true
            };
        });

        if (printed) {
            textboxPropsArray[exceptions.length] = {
                caption: "Status",
                text: "Invoice has been printed",
                displayType: DisplayType.STRING,
                rowBreak: true,
                fillRow: true
            };
        }
        CommonTooltips.setTooltipContentTextboxes(this.imageNotifications, () => textboxPropsArray);
    }

    afterCustomerExecution() {
        this.setBridgeCustomerLabel();
    }

    setBridgeCustomerLabel() {
        const bridgeId = this.sourceCustomer?.activeRow?.get("bridge_id");
        if (bridgeId) {
            this.labelBridgeCustomer.visible = true;
        } else {
            this.labelBridgeCustomer.visible = false;
        }
    }

    textboxCommodityIdOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && StringUtil.isEmptyString(event.newValue)) {
            this.activeRow.set("commodity_descr", null);
            this.textboxCommodity.text = null;
        }
    }

    textboxCommodityIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const commId = event.selection.get("id");
            this.activeRow.set("commodity", commId);
            const billing = this.owner as Billing;
            billing.textboxCommodityId.text = commId;
            const descr = event.selection.get("descr");
            this.activeRow.set("commodity_descr", descr);
            this.textboxCommodity.text = descr;
        }
    }

    panelStopInfoOnClick(event: ClickEvent) {
        const billing = this.owner as Billing;
        billing.tabRate.scrollIntoView();

    }

    bolOnClick(event: ClickEvent) {
        this.textboxBlnum.enabled = true;
        this.textboxBlnum.printable = false;
    }

    consRefnoOnClick(event: ClickEvent) {
        this.textboxConsigneeRefNo.enabled = true;
        this.textboxConsigneeRefNo.printable = false;
    }

    shipIdOnClick(event: ClickEvent) {
        this.textboxShipmentId.enabled = true;
        this.textboxShipmentId.printable = false;
    }

    private setDistanceWarningComponentsToDefault(distanceWarningImage: Image, panelDistance: Panel) {
        distanceWarningImage.visible = false;
        for (const component of panelDistance.components) {
            const label = component as Label;
            if (!StringUtil.isEmptyString(label.field)) {
                label.color = "default";
            }
        }
        const labelMI = this.panelBillingDistance.findComponentById("labelMI") as Label;
        if (labelMI) {
            labelMI.color = "default";
        }
        this.suppressLabelTooltip(panelDistance);
    }

    private createLabelHoverToDiscover(label: Label, row: any) {
        label.tooltipPosition = Alignment.RIGHT;
        const panel = new Panel({ id: "panelHoverToDisplay", maxWidth: 400, cursor: Cursor.DEFAULT }) as Panel;
        const labelOne = new Label({ id: "hoverIdOne", fontBold: true, fontSize: "xlarge", text: `Distance lookup warning`, rowBreak: false });
        const labelTwo = new Label({ id: "hoverIdTwo", text: "" + row?.get("comments"), rowBreak: true });
        panel.add(labelOne);
        panel.add(labelTwo);

        label.tooltipDynamicContent = panel;
    }

    private suppressLabelTooltip(panelDistance: Panel) {
        for (const component of panelDistance.components) {
            const label = component as Label;
            label.allowTooltip = false;
        }
    }
}
